import React, { useState, useEffect } from "react";
import ReactDOMServer from "react-dom/server";
import { Throwable } from "@wss/error-tracking/throwable";
import Notification from "~/common/Notification";
import { Product, SelectedProduct } from "~/common/Store/models/ProductListing";
import { UserState } from "~/common/Store/models/RootReadonly";
import VirtualGroupingSelect from "./VirtualGroupingSelect";
import PurchasingGroupMinimumQuantityView from "~/productListing/ProductBox/AddToCart/PurchasingGroupMinimumQuantityView";
import { PurchasingGroupProps } from "~/productListing/PurchasingGroupPage/purchasingGroupPageResult";
import setUserSelectedProduct from "../setSelectedProduct";
import ButtonWrapper from "./ButtonWrapper";
import {
  addToCart,
  getErrorMessage,
  qty,
  useAddToCart,
  doesAnyGroupMembersHasMustPurchaseMoreThanOne,
  getItemsFromUrl,
  getMatchingItemId,
  getSelectedGroupMember,
  getProductImageAndDescription,
  getLastQuantityInterval,
  AddToCartResultData,
  validateQuantity,
  CloseNotificationOnError,
  shouldForceQuantityToOne,
  ProductBoxType,
} from "./logic";
import useAddToCartModals from "~/common/AddToCartModal/useAddToCartModals";
import { incrementCount } from "./QuantityDropdown";
import shouldDisplayRequiredCustomization from "~/common/Product/shouldDisplayRequiredCustomization";
import fetchAccessories from "~/ProductDetails/Carousel/fetchAccessories";
import CartLimitError from "./CartLimitError";
import Store from "~/common/Store";
import { incrementItemsInCart } from "~/common/Store/actions/user";
import SuccessMessage from "../SuccessMessage";
import { markProductViewed } from "~/userAnalytics";
import ProductModals, { ProductModalData } from "./ProductModals";
import { AddToCartModalNames } from "~/common/AddToCartModal";
import { getCarouselProps } from "~/analytics/AddToCart/carouselAddToCartAnalytics";
import useWindowDimensions from "~/common/Header/hooks/useWindowDimensions";
import useIsMounted from "~/common/useIsMounted";
import OutOfStockLabel from "./Components/OutOfStockLabel";
import LimitedSaleStockNoticeWrapper from "./Components/LimitedSaleStockNoticeWrapper";
import AdditionalControl from "./Components/AdditionalControl";
import TopProductsAddToCartErrorMessage from "./Components/TopProductsAddToCartErrorMessage";
import EmailButtonWrapper from "./Components/EmailButtonWrapper";
import GroupingSelectWrapper from "./Components/GroupingSelectWrapper";
import AddToCartErrorMessageWrapper from "./Components/AddToCartErrorMessageWrapper";

export type ProductListingView =
  | "list"
  | "grid"
  | "topProductsCarousel"
  | undefined;

interface AddToCartProps {
  product: Product;
  user: UserState;
  setSelectedProduct: React.Dispatch<React.SetStateAction<SelectedProduct>>;
  selectedProduct: SelectedProduct;
  isCarouselProductBox?: boolean;
  isCompareWithProductBox?: boolean;
  isOnCompareProductsPage?: boolean;
  hasCustomizableProductsOverride?: boolean;
  purchasingGroupProps?: PurchasingGroupProps;
  setUserHasSelectedGroupMember?: React.Dispatch<React.SetStateAction<boolean>>;
  useBlockStyleAddToCart?: boolean;
  isItemAlreadyInCart?: boolean;
  productListingView?: ProductListingView;
  outOfStockStyle?: string;
  discountCode?: string;
  showSaleStockNotice?: boolean;
  preselectedVgMemberItemNumber?: string;
  selectedAccessoryIds?: string[];
  validateAccessories?: () => boolean;
  groupMemberSelected?: boolean;
  previouslyPurchased?: boolean;
  defaultItemNumber?: string;
}

interface CompleteAddToCartProps {
  accessoriesExist: boolean;
  addToCartData: AddToCartResultData;
  discountCode: string | undefined;
  openModals: (modals: AddToCartModalNames[]) => void;
  product: Product;
  quantityVal: number;
  selectedAccessories: string[] | undefined;
  setCartLimitMessage: React.Dispatch<React.SetStateAction<string>>;
  setIsCartModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  virtualGroupingItemNumber: string | undefined;
  eventTarget?: EventTarget | null;
}

const completeAddToCart = async ({
  accessoriesExist,
  addToCartData,
  discountCode,
  openModals,
  product,
  quantityVal,
  selectedAccessories,
  setCartLimitMessage,
  setIsCartModalOpen,
  virtualGroupingItemNumber,
  eventTarget,
}: CompleteAddToCartProps) => {
  const {
    groupingValue,
    hasAcknowledgedOutletItem,
    hasAcknowledgedProductWarning,
    setError,
    setItemIsInCart,
  } = addToCartData;

  setError(undefined);

  const result: Throwable | void = await addToCart({
    virtualGroupingItemNumber,
    selectedAccessories,
    product,
    groupingValue,
    hasAccessories: accessoriesExist,
    hasAcknowledgedOutletItem,
    hasAcknowledgedProductWarning,
    quantity: quantityVal,
    openModals,
    discountCode,
    gtmAtcProps: {
      source: "product listing",

      carouselProps: getCarouselProps(eventTarget as HTMLElement),
    },
  });

  if (!result) return;

  if (result.isError) {
    if (result.error instanceof CartLimitError) {
      setCartLimitMessage(result.error.message);
      setIsCartModalOpen(true);
    } else {
      setError(result.error);
    }
  } else {
    Store.dispatch(incrementItemsInCart(quantityVal));

    const { selectedImage, selectedDescription } =
      getProductImageAndDescription(product, groupingValue);

    new Notification(
      {
        imageUrl:
          selectedImage || "/images/products/new/imagecomingsoon_tn.jpg",
        heading: ReactDOMServer.renderToString(
          <SuccessMessage quantity={quantityVal} />,
        ),
        href: "/viewcart.cfm",
        body: selectedDescription || "",
        buttonText: "View Cart",
        secondButtonText: "Checkout",
        secondHref: "/viewinfo.cfm",
      },
      "default",
    );

    // move keyboard focus into notification when it is opened
    const watNotif = document.getElementById("watnotif-wrapper");

    const firstNotif = watNotif?.children[0] as HTMLElement;
    const notifDescId = selectedDescription?.replace(/\s+/g, "");

    if (notifDescId && firstNotif) {
      firstNotif.setAttribute("tabindex", "-1");
      firstNotif.setAttribute("aria-labelledby", notifDescId);
      firstNotif.focus();
    }

    setItemIsInCart?.(true);
  }
};

const getStartBuy = (product: Product) => {
  const { mustBuy, startBuy } = product;

  return mustBuy > startBuy ? mustBuy : startBuy;
};

const handleGroupingForCompareProducts = (
  product: Product,
  isOnCompareProductsPage: boolean,
  setGroupingValue: React.Dispatch<string>,
) => {
  if (!isOnCompareProductsPage) {
    return;
  }

  const itemIds = getItemsFromUrl();

  if (!itemIds.length) return;

  const matchingItemId = getMatchingItemId(itemIds, product);
  if (matchingItemId) {
    setGroupingValue(matchingItemId);
  }
};

const loadAccessories = async (
  hasAccessories: boolean,
  hasMultipleVGGrouping: boolean,
  itemNumber: string,
) => {
  if (!hasAccessories || hasMultipleVGGrouping) {
    return [];
  }

  const accessoryData = await fetchAccessories(itemNumber);

  const hasAvailableAccessories = accessoryData.some(
    (accessory) =>
      accessory.isRequired &&
      accessory.options.some(
        (option) => option.available && option.useStandardInterface,
      ),
  );

  if (!hasAvailableAccessories) {
    return [];
  }

  return accessoryData;
};

const AddToCart = ({
  product,
  user,
  setSelectedProduct,
  selectedProduct,
  isCarouselProductBox = false,
  isCompareWithProductBox = false,
  isOnCompareProductsPage = false,
  hasCustomizableProductsOverride = false,
  purchasingGroupProps,
  setUserHasSelectedGroupMember,
  useBlockStyleAddToCart = false,
  isItemAlreadyInCart = false,
  productListingView,
  outOfStockStyle,
  discountCode,
  showSaleStockNotice = false,
  preselectedVgMemberItemNumber = "",
  selectedAccessoryIds,
  validateAccessories,
  groupMemberSelected,
  previouslyPurchased = false,
  defaultItemNumber,
}: Readonly<AddToCartProps>) => {
  const {
    outletAttributes,
    mustBuy,
    groupingAttributes,
    grouping,
    itemNumber,
    mustPurchaseMoreThanOne,
    hasAccessories,
    warningContent,
    isReplacementEligible = false,
    isRequestAQuoteEnabled,
  } = product;
  const mounted = useIsMounted();
  const { width } = useWindowDimensions(mounted);
  const addToCartData = useAddToCart({ product });
  const [isCartModalOpen, setIsCartModalOpen] = useState(false);
  const [cartLimitMessage, setCartLimitMessage] = useState("");

  const {
    accessories,
    setAccessories,
    error,
    setError,
    setItemIsInCart,
    itemIsInCart,
    setQuantity,
    quantity,
    groupingValue,
    setGroupingValue,
    hasMultipleVGGrouping,
    hasAcknowledgedOutletItem,
    hasAcknowledgedProductWarning,
  } = addToCartData;

  const quantityVal = qty(quantity);

  const { openModals, activeModal, closeModal, submitModal } =
    useAddToCartModals({
      onComplete: (results, openModals) => {
        const accessoriesExist = hasMultipleVGGrouping
          ? (results.virtualGrouping?.hasAccessories ?? false)
          : accessories.length > 0;

        completeAddToCart({
          accessoriesExist,
          addToCartData,
          discountCode,
          openModals,
          product,
          quantityVal,
          selectedAccessories: selectedAccessoryIds ?? results.accessories,
          setCartLimitMessage,
          setIsCartModalOpen,
          virtualGroupingItemNumber:
            results.virtualGrouping?.groupMemberItemNumber,
        });
      },
    });

  const displayRequiredCustomization = shouldDisplayRequiredCustomization(
    groupMemberSelected ? selectedProduct : product,
  );

  const handleQuantityChange = (value: string) => {
    const inputValue = value;
    let quantityValue = inputValue;

    if (
      mustPurchaseMoreThanOne &&
      inputValue === lastQuantityInterval.toString()
    ) {
      if (!forceCustomQuantity) {
        setForceCustomQuantity(true);
        quantityValue = startBuy.toString();
      }
    } else if (anyGroupMembersHasMustPurchaseMoreThanOne) {
      const selectedGroupMember = getSelectedGroupMember(
        product,
        groupingValue,
      );
      if (
        shouldForceQuantityToOne(
          inputValue,
          forceCustomQuantity,
          lastQuantityInterval,
          selectedGroupMember,
        )
      ) {
        setForceCustomQuantity(true);
        quantityValue = "1";
      }
    }

    setQuantity(quantityValue);

    validateQuantity(quantityValue, product, addToCartData);
  };

  const { isInStock, isOutlet } = selectedProduct;

  const [itemQuantityVisible, setItemQuantityVisible] = useState(false);
  const [firedProductImpressions, setFiredProductImpressions] = useState({});

  const handleQuantityIsVisible = () => {
    if (!itemQuantityVisible) {
      setItemQuantityVisible(true);
    }
  };

  const modalData: ProductModalData = {
    accessories,
    hasAccessories,
    setAccessories,
    activeModal,
    closeModal,
    submitModal,
    hasAcknowledgedProductWarning,
    hasAcknowledgedOutletItem,
    cartLimitMessage,
    isCartModalOpen,
    setIsCartModalOpen,
    groupingAttributes,
    outletAttributes,
    warningContent,
    previouslyPurchased,
    itemNumber,
    selectedAccessoryIds,
    hasCustomizableProductsOverride,
    displayRequiredCustomization,
    quantity,
    handleQuantityChange,
    isOutlet,
    itemQuantityVisible,
  };

  useEffect(() => {
    if (preselectedVgMemberItemNumber.length > 0) {
      setGroupingValue(preselectedVgMemberItemNumber);
    }

    setItemIsInCart(isItemAlreadyInCart);
  }, [
    isItemAlreadyInCart,
    setItemIsInCart,
    setGroupingValue,
    preselectedVgMemberItemNumber,
  ]);

  const buttonBlockStyle = useBlockStyleAddToCart ? " btn-block" : "";

  const startBuy = getStartBuy(product);

  const [forceCustomQuantity, setForceCustomQuantity] = useState(false);

  const isVirtualItem = groupingAttributes.isVirtual;

  const anyGroupMembersHasMustPurchaseMoreThanOne =
    doesAnyGroupMembersHasMustPurchaseMoreThanOne(groupingAttributes);

  let lastQuantityInterval = getLastQuantityInterval(
    startBuy,
    mustBuy,
    incrementCount,
  );

  const errorMessage = getErrorMessage(error);

  const showVirtualGroupSelect =
    grouping && isVirtualItem && !hasMultipleVGGrouping;

  useEffect(() => {
    handleGroupingForCompareProducts(
      product,
      isOnCompareProductsPage,
      setGroupingValue,
    );
  });

  const handleGroupingChange = async (
    event: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>,
  ) => {
    const newGroupingValue = event.target.value;
    setGroupingValue(newGroupingValue);
    setForceCustomQuantity(false);
    setQuantity("1");
    setError(undefined);

    const selectedGroupMember = product.groupingAttributes.groupMembers.find(
      (groupMember) => groupMember.itemNumber === newGroupingValue,
    );

    if (!selectedGroupMember) {
      return;
    }

    setUserSelectedProduct(product, selectedGroupMember, setSelectedProduct);

    if (setUserHasSelectedGroupMember === undefined) {
      return;
    }

    if (
      !(selectedGroupMember.itemNumber in firedProductImpressions) &&
      defaultItemNumber !== selectedGroupMember.itemNumber
    ) {
      const groupMemberItemNumber = selectedGroupMember.itemNumber;

      const trackedProductImpressions: { [key: string]: boolean } = {
        ...firedProductImpressions,
      };

      trackedProductImpressions[groupMemberItemNumber] = true;
      await markProductViewed(selectedGroupMember.itemNumber);

      setFiredProductImpressions(trackedProductImpressions);
    }

    setUserHasSelectedGroupMember(true);
  };

  const handleAddToCart = async (event: React.SyntheticEvent) => {
    event.preventDefault();

    if (validateAccessories !== undefined && !validateAccessories()) {
      return;
    }

    const accessoryData = await loadAccessories(
      hasAccessories,
      hasMultipleVGGrouping,
      selectedProduct.itemNumber,
    );

    setAccessories(accessoryData);

    await completeAddToCart({
      accessoriesExist: hasMultipleVGGrouping
        ? product.hasAccessories
        : !!accessoryData.length,
      addToCartData,
      discountCode,
      openModals,
      product,
      quantityVal,
      selectedAccessories: undefined,
      setCartLimitMessage,
      setIsCartModalOpen,
      virtualGroupingItemNumber: undefined,
      eventTarget: event.target,
    });
  };

  useEffect(() => {
    CloseNotificationOnError(error);
  }, [error]);

  const itemWithNoGroupingMustPurchasemoreThanOne =
    mustPurchaseMoreThanOne && !grouping;

  const selectedGroupMember =
    anyGroupMembersHasMustPurchaseMoreThanOne &&
    grouping &&
    groupingValue &&
    product.groupingAttributes.groupMembers.find(
      (groupMember) => groupMember.itemNumber === groupingValue,
    );

  const showQuantityDropdown =
    (itemWithNoGroupingMustPurchasemoreThanOne ||
      !!(selectedGroupMember && selectedGroupMember.mustPurchaseMoreThanOne)) &&
    !forceCustomQuantity;

  if (selectedGroupMember && selectedGroupMember.mustPurchaseMoreThanOne) {
    lastQuantityInterval =
      selectedGroupMember.startBuy +
      (incrementCount - 1) * selectedGroupMember.mustBuy;
  }

  const isCustomizeNowProductBox =
    !isOutlet &&
    (hasCustomizableProductsOverride || displayRequiredCustomization);

  const productBoxType: ProductBoxType = {
    isCarouselProductBox,
    isCompareWithProductBox,
    isCustomizeNowProductBox,
  };

  return (
    <>
      <ProductModals {...modalData} />
      {purchasingGroupProps?.isPurchasingGroup ? (
        <PurchasingGroupMinimumQuantityView
          {...{
            product,
            user,
            isReplacementEligible,
            setPurchasingGroupItemsSelected:
              purchasingGroupProps.setPurchasingGroupItemsSelected,
          }}
        />
      ) : (
        <div className={`add-to-cart${buttonBlockStyle}`}>
          <form action="/viewcart.cfm" method="post">
            <TopProductsAddToCartErrorMessage
              {...{
                errorMessage,
                itemNumber,
                productListingView,
              }}
            />
            <ButtonWrapper
              {...{
                product,
                productBoxType,
                productListingView,
                width,
                displayRequiredCustomization,
                hasCustomizableProductsOverride,
                isOutlet,
              }}
            >
              {showVirtualGroupSelect && (
                <VirtualGroupingSelect
                  {...{
                    product,
                    groupingValue,
                    handleGroupingChange,
                    isCompareWithProductBox,
                    isCarouselProductBox,
                    isCustomizeNowProductBox,
                    hasCustomizableProductsOverride,
                    showError: !!errorMessage,
                    user,
                    isOnCompareProductsPage,
                  }}
                />
              )}
              <GroupingSelectWrapper
                {...{
                  groupingValue,
                  handleGroupingChange,
                  isCarouselProductBox,
                  isCompareWithProductBox,
                  isOnCompareProductsPage,
                  isVirtualItem,
                  product,
                  productListingView,
                }}
              />
              <AdditionalControl
                {...{
                  displayRequiredCustomization,
                  forceCustomQuantity,
                  groupingValue,
                  groupMemberSelected,
                  handleAddToCart,
                  handleQuantityChange,
                  handleQuantityIsVisible,
                  hasCustomizableProductsOverride,
                  hasMultipleVGGrouping,
                  incrementCount,
                  isOnCompareProductsPage,
                  isOutlet,
                  isRequestAQuoteEnabled,
                  isVirtualItem,
                  itemIsInCart,
                  lastQuantityInterval,
                  previouslyPurchased,
                  product,
                  productBoxType,
                  productListingView,
                  quantity,
                  selectedGroupMember,
                  selectedProduct,
                  error,
                  setError,
                  showQuantityDropdown,
                  startBuy,
                }}
              />

              <OutOfStockLabel
                {...{
                  isInStock,
                  isReplacementEligible,
                  isCarouselProductBox,
                  outOfStockStyle,
                }}
              />
            </ButtonWrapper>
            <AddToCartErrorMessageWrapper
              {...{
                errorMessage,
                isInStock,
                productListingView,
              }}
            />
          </form>
          <EmailButtonWrapper
            {...{
              hasCustomizableProductsOverride,
              isCarouselProductBox,
              product,
              selectedProduct,
              groupingValue,
              user,
            }}
          />

          <LimitedSaleStockNoticeWrapper
            {...{ product, productListingView, showSaleStockNotice }}
          />
        </div>
      )}
    </>
  );
};

export default AddToCart;
